import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import type { Usuario } from '../../api-client/usuarios/usuario';
import type {
  ActualizarUsuarioRequest,
  RegistrarUsuarioRequest
} from '../../api-client/usuarios/usuarios.api';
import { usePerfilesStore } from '../../stores/perfiles/perfiles.store';
import { useUIStore } from '../../stores/ui/ui.store';
import type { CommonEntityFormProps } from '../../utils/common-entity-form-props';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import transformToOptions from '../../utils/transform-to-options';
import AppTextField from '../ui/AppTextField';
import ComboBox from '../ui/Combobox';
import EditForm from '../ui/EditForm';
import LabeledCheckBox from '../ui/LabeledCheckBox';

const validationSchema = yup.object({
  id: yup.string().required('La clave es requerida'),
  nombre: yup.string().required('El nombre es requerido'),
  actualizarPassword: yup.boolean(),
  idPerfil: yup.number().min(1, 'El perfil es requerido'),
  password: yup.string().when('actualizarPassword', {
    is: true,
    then: (schema) => schema.required('La contraseña es requerida'),
    otherwise: (schema) => schema.notRequired()
  }),
  confirma: yup.string().when('actualizarPassword', {
    is: true,
    then: (schema) =>
      schema
        .required('La confirmación es requerida')
        .oneOf([yup.ref('password')], 'La contraseña y su confirmación deben coincidir'),
    otherwise: (schema) => schema.notRequired()
  })
});

type UsuarioFormProps = {
  usuario?: Usuario;
  onSave:
    | ((values: ActualizarUsuarioRequest) => Promise<void>)
    | ((values: RegistrarUsuarioRequest) => Promise<boolean>);
  self?: boolean;
  source?: string | null;
} & CommonEntityFormProps;

const UsuarioForm = ({
  usuario,
  esAgregar,
  onSave,
  onDelete,
  onActivate,
  self,
  source
}: UsuarioFormProps) => {
  const esEditar = !esAgregar;
  const activo = esAgregar || usuario?.activo === true;
  const inactivo = !activo;
  const status = activo ? 'ACTIVO' : 'INACTIVO';
  const { loading } = useUIStore();
  const { actives: perfiles, cargarActivos } = usePerfilesStore();
  const initialValues = {
    id: usuario?.id ?? '',
    nombre: usuario?.nombre ?? '',
    actualizarPassword: !!esAgregar,
    idPerfil: usuario?.idPerfil ?? 0,
    password: '',
    confirma: ''
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const success = await onSave(values);
      if (success) {
        formik.resetForm();
      }
    },
    enableReinitialize: true
  });

  useEffect(() => {
    cargarActivos();
  }, []);

  const perfilesOptions = transformToOptions(perfiles);

  const reset = () => formik.resetForm({ values: initialValues });

  const returnLink = source !== 'menu' ? '/usuarios' : '/';

  return (
    <EditForm
      module="usuarios"
      title="Usuarios"
      readonly={!activo}
      returnLink={returnLink}
      loading={loading}
      showActivate={false}
      showDelete={esEditar && !self}
      onSave={formik.handleSubmit}
      onClear={reset}
      onDelete={onDelete}
      onActivate={onActivate}
    >
      <Grid>
        <Grid container spacing={2}>
          <Grid>
            <AppTextField
              name="id"
              label="Clave"
              placeholder="Clave"
              maxLength={10}
              uppercase={true}
              value={formik.values.id}
              readOnly={esEditar}
              disabled={loading}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.id && Boolean(formik.errors.id)}
              helperText={getLengthOrErrorText(
                formik.touched.id ? formik.errors.id : '',
                formik.values.id.length,
                10
              )}
            />
          </Grid>
          <Grid size="grow">
            <AppTextField
              name="nombre"
              label="Nombre"
              placeholder="Nombre"
              maxLength={65}
              disabled={loading || inactivo}
              value={formik.values.nombre}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.nombre && Boolean(formik.errors.nombre)}
              helperText={getLengthOrErrorText(
                formik.touched.nombre ? formik.errors.nombre : '',
                formik.values.nombre.length,
                65
              )}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Grid container spacing={2}>
          <Grid size={4}>
            <ComboBox
              label="Perfil"
              name="idPerfil"
              disabled={loading || inactivo}
              value={formik.values.idPerfil}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              options={perfilesOptions}
              error={formik.touched.idPerfil && Boolean(formik.errors.idPerfil)}
              helperText={formik.touched.idPerfil ? formik.errors.idPerfil : ''}
            />
          </Grid>
          {!esAgregar && (
            <Grid size={2}>
              <LabeledCheckBox
                label="Actualizar contraseña"
                name="actualizarPassword"
                disabled={loading || inactivo}
                checked={formik.values.actualizarPassword}
                onChange={formik.handleChange}
              />
            </Grid>
          )}
          <Grid size={2}>
            <AppTextField
              name="password"
              label="Contraseña"
              placeholder="Contraseña"
              maxLength={10}
              isPassword={true}
              disabled={loading || inactivo || !formik.values.actualizarPassword}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={getLengthOrErrorText(
                formik.touched.password ? formik.errors.password : '',
                formik.values.password.length,
                10
              )}
            />
          </Grid>
          <Grid size={2}>
            <AppTextField
              name="confirma"
              label="Confirme"
              placeholder="Confirme"
              maxLength={10}
              isPassword={true}
              disabled={loading || inactivo || !formik.values.actualizarPassword}
              value={formik.values.confirma}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirma && Boolean(formik.errors.confirma)}
              helperText={getLengthOrErrorText(
                formik.touched.confirma ? formik.errors.confirma : '',
                formik.values.confirma.length,
                10
              )}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Grid container spacing={2}>
          <Grid size={2}>
            <AppTextField label="Status" value={status} disabled={true} />
          </Grid>
        </Grid>
      </Grid>
    </EditForm>
  );
};

export default UsuarioForm;
